import { createPortal } from "react-dom";
import React, { useState, Fragment, useEffect, useRef, useCallback } from "react";
import { Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import useApiToken from "../../../hooks/useApiToken";
import { editCompany, searchCities } from "../../../api/ranking.api";

interface CityOption {
  id: string;
  name: string;
}

interface CompanyEditData {
  id: string;
  name: string;
  url: string;
  linkedIn: string;
  cityIds: string[];
  revenue: string;
  stockMarket: string;
  relevance: string;
  hotStartup: string;
}

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSave: () => void;
  companyData: any;
}

const STOCK_MARKET_OPTIONS = ["Listed In India", "Listed Outside India", "No"];
const RELEVANCE_OPTIONS = ["High", "Low"];
const HOT_STARTUP_OPTIONS = ["Yes", "No"];

const EditCompanyModal = ({ isOpen, onCancel, onSave, companyData }: Props) => {
  const token = useApiToken();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState<CompanyEditData>({
    id: "",
    name: "",
    url: "",
    linkedIn: "",
    cityIds: [],
    revenue: "",
    stockMarket: "",
    relevance: "",
    hotStartup: "",
  });

  const [selectedCities, setSelectedCities] = useState<CityOption[]>([]);
  const [citySearch, setCitySearch] = useState("");
  const [cityResults, setCityResults] = useState<CityOption[]>([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [searchingCities, setSearchingCities] = useState(false);
  const citySearchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (companyData && isOpen) {
      const cities: CityOption[] = companyData.locations
        ? companyData.locations.map((l: any) => ({ id: l.id, name: l.name }))
        : [];
      setFormData({
        id: companyData.id || "",
        name: companyData.name || "",
        url: companyData.url || "",
        linkedIn: companyData.linkedIn || "",
        cityIds: cities.map((c) => c.id),
        revenue: companyData.revenue || "",
        stockMarket: companyData.stockMarket || "",
        relevance: companyData.relevance || "",
        hotStartup: companyData.hotStartup || "",
      });
      setSelectedCities(cities);
      setError("");
      setCitySearch("");
      setCityResults([]);
    }
  }, [companyData, isOpen]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflowY = "hidden";
    } else {
      document.body.style.overflowY = "auto";
    }
    return () => {
      document.body.style.overflowY = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (citySearchRef.current && !citySearchRef.current.contains(e.target as Node)) {
        setShowCityDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleCitySearch = useCallback(
    (query: string) => {
      setCitySearch(query);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (query.trim().length < 2) {
        setCityResults([]);
        setShowCityDropdown(false);
        return;
      }
      debounceRef.current = setTimeout(async () => {
        if (!token) return;
        setSearchingCities(true);
        try {
          const results = await searchCities(query.trim(), token);
          const mapped: CityOption[] = results.map((r: any) => ({
            id: r.id,
            name: r.name,
          }));
          const filtered = mapped.filter(
            (c) => !selectedCities.some((sc) => sc.id === c.id)
          );
          setCityResults(filtered);
          setShowCityDropdown(true);
        } catch {
          setCityResults([]);
        } finally {
          setSearchingCities(false);
        }
      }, 300);
    },
    [token, selectedCities]
  );

  const addCity = (city: CityOption) => {
    const updated = [...selectedCities, city];
    setSelectedCities(updated);
    setFormData((prev) => ({ ...prev, cityIds: updated.map((c) => c.id) }));
    setCitySearch("");
    setCityResults([]);
    setShowCityDropdown(false);
  };

  const removeCity = (cityId: string) => {
    const updated = selectedCities.filter((c) => c.id !== cityId);
    setSelectedCities(updated);
    setFormData((prev) => ({ ...prev, cityIds: updated.map((c) => c.id) }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!token) return;
    setSaving(true);
    setError("");
    try {
      await editCompany(formData, token);
      onSave();
      onCancel();
    } catch (err: any) {
      setError(err.message || "Failed to update company");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <Transition.Root show={isOpen} as={Fragment}>
      <Transition.Child
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enterTo="opacity-100 translate-y-0 sm:scale-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      >
        <div
          className={`${
            isOpen ? "transition-all" : "hidden"
          } fixed top-0 right-0 left-0 bottom-0 w-[100vw] z-20`}
        >
          <div
            className="absolute bg-gray-400 opacity-40 top-0 w-full h-screen z-10"
            onClick={onCancel}
          ></div>
          <div
            className="absolute top-0 right-0 bottom-0 left-0 max-w-[550px] max-h-max m-auto z-50 p-4"
          >
            <header className="bg-gray-100 dark:bg-gray-700 rounded-t-xl p-2 text-center text-gray-700 dark:text-gray-400 font-medium">
              Edit Company
            </header>
            <section className="flex flex-col bg-white dark:bg-darkbglight p-6 overflow-auto gap-4 max-h-[70vh]">
              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[120px] text-gray-700 dark:text-gray-300">
                  Name:
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="placeholder:text-xs px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[120px] text-gray-700 dark:text-gray-300">
                  Website:
                </label>
                <input
                  type="text"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="https://example.com"
                  className="placeholder:text-xs px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[120px] text-gray-700 dark:text-gray-300">
                  LinkedIn:
                </label>
                <input
                  type="text"
                  name="linkedIn"
                  value={formData.linkedIn}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/company/..."
                  className="placeholder:text-xs px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex items-start gap-4">
                <label className="text-sm font-semibold w-[120px] text-gray-700 dark:text-gray-300 mt-1">
                  Locations:
                </label>
                <div className="flex-1" ref={citySearchRef}>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {selectedCities.map((city) => (
                      <span
                        key={city.id}
                        className="inline-flex items-center gap-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-800 dark:text-indigo-200 text-xs px-2 py-1 rounded-full"
                      >
                        {city.name}
                        <button
                          type="button"
                          onClick={() => removeCity(city.id)}
                          className="hover:text-red-500"
                        >
                          <XMarkIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={citySearch}
                      onChange={(e) => handleCitySearch(e.target.value)}
                      placeholder="Search cities..."
                      className="placeholder:text-xs px-2 py-1 w-full border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                    />
                    {showCityDropdown && (
                      <div className="absolute z-50 mt-1 w-full max-h-40 overflow-auto bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl shadow-lg">
                        {searchingCities ? (
                          <div className="px-3 py-2 text-xs text-gray-500">Searching...</div>
                        ) : cityResults.length > 0 ? (
                          cityResults.map((city) => (
                            <button
                              key={city.id}
                              type="button"
                              onClick={() => addCity(city)}
                              className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-gray-600 dark:text-white"
                            >
                              {city.name}
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-xs text-gray-500">No cities found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[120px] text-gray-700 dark:text-gray-300">
                  Revenue:
                </label>
                <input
                  type="text"
                  name="revenue"
                  value={formData.revenue}
                  onChange={handleChange}
                  placeholder="e.g. $54.762B"
                  className="placeholder:text-xs px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[120px] text-gray-700 dark:text-gray-300">
                  Stock Market:
                </label>
                <select
                  name="stockMarket"
                  value={formData.stockMarket}
                  onChange={handleChange}
                  className="px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select</option>
                  {STOCK_MARKET_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[120px] text-gray-700 dark:text-gray-300">
                  Relevance:
                </label>
                <select
                  name="relevance"
                  value={formData.relevance}
                  onChange={handleChange}
                  className="px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select</option>
                  {RELEVANCE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm font-semibold w-[120px] text-gray-700 dark:text-gray-300">
                  Hot Startup:
                </label>
                <select
                  name="hotStartup"
                  value={formData.hotStartup}
                  onChange={handleChange}
                  className="px-2 py-1 flex-1 border-gray-300 rounded-xl text-sm dark:bg-gray-700 dark:text-white"
                >
                  <option value="">Select</option>
                  {HOT_STARTUP_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {error && (
                <span className="text-xs text-red-500">*{error}</span>
              )}
            </section>
            <footer className="bg-gray-100 dark:bg-gray-700 rounded-b-xl p-2 text-center text-gray-700 dark:text-gray-400 font-medium">
              <div className="flex justify-end gap-3">
                <button
                  className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                    saving ? "bg-gray-400" : "bg-indigo-600 hover:bg-indigo-700"
                  }`}
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  className="inline-flex items-center rounded-md bg-red-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </div>
            </footer>
          </div>
        </div>
      </Transition.Child>
    </Transition.Root>,
    document.body
  );
};

export default EditCompanyModal;
